import * as p from '@clack/prompts'
import { stripVTControlCharacters } from 'node:util'
import type { Spinner } from '@cli-types/core-engine/utils.js'
import {
  CLACK_LINE_ROWS,
  GREEN_CHECK,
  GREEN_STEP_SUBMIT,
  LOG_PREFIX_WIDTH,
  MIN_MESSAGE_WIDTH,
  RENDER_OVERHEAD
} from '@cli-constants/prompt-theme.js'

export function isInteractiveSession(): boolean {
  return Boolean(process.stdin.isTTY) && process.env.CI !== 'true'
}

/**
 * stdin being a TTY is not enough: with stdout piped or captured
 * (`betterstart | cat`, `out=$(betterstart)`) the prompt renders into the pipe
 * while still waiting on stdin, so the caller hangs on input it cannot see.
 */
export function isInteractiveTerminalSession(): boolean {
  return isInteractiveSession() && Boolean(process.stdout.isTTY)
}

/** Off-TTY the terminal width is unknown; mirror clack's own 80-column assumption. */
export function fitSpinnerMessage(message: string): string {
  // Tabs render up to eight columns while every width measurement here (and
  // clack's own wrap) counts them as one, so an animated line containing a
  // tab overflows the row it was measured to fit — flatten them first.
  const normalized = message.replace(/\t/g, ' ')
  const columns = process.stdout.columns ?? 80
  const max = Math.max(columns - RENDER_OVERHEAD, MIN_MESSAGE_WIDTH)
  const visible = stripVTControlCharacters(normalized)
  if (visible.length <= max) return normalized
  // Truncate the color-stripped text so the slice cannot cut an escape
  // sequence in half.
  return `${visible.slice(0, max - 1)}…`
}

/**
 * Rows a settled clack text, password, or confirm prompt occupies: its leading
 * guide row, the prompt message, and the submitted value. Both content lines
 * may wrap after clack's three-column symbol/guide prefix.
 */
export function clackPromptRows(message: string, value: string): number {
  const columns = process.stdout.columns ?? 80
  const rows = (text: string) =>
    Math.max(1, Math.ceil((stripVTControlCharacters(text).length + LOG_PREFIX_WIDTH) / columns))
  return 1 + rows(message) + rows(value)
}

/**
 * Counts the `│` guide row clack prints plus the rows the message wraps to.
 * Off-TTY mirrors clack's 80-column assumption.
 */
export function clackLogRows(message: string): number {
  const columns = process.stdout.columns ?? 80
  const width = stripVTControlCharacters(message).length + LOG_PREFIX_WIDTH
  return 1 + Math.max(1, Math.ceil(width / columns))
}

/**
 * Erase the last `rows` terminal rows via ANSI delete-line; the cursor must be
 * at the start of the line just below them, where it ends up again. No-op
 * off-TTY (CI, pipes) where cursor movement would corrupt plain output.
 */
export function eraseRows(rows: number): void {
  if (rows <= 0 || !process.stdout.isTTY || process.env.CI === 'true') return
  process.stdout.write(`\x1b[${rows}A\x1b[${rows}M`)
}

/**
 * Delete terminal rows that have settled output below them, shifting the later
 * output upward and restoring the cursor below it. No-op off-TTY and under CI.
 */
export function eraseRowsAbove(rows: number, rowsBelow: number): void {
  if (rows <= 0 || rowsBelow < 0 || !process.stdout.isTTY || process.env.CI === 'true') {
    return
  }
  const up = rows + rowsBelow
  const restore = rowsBelow > 0 ? `\x1b[${rowsBelow}B` : ''
  process.stdout.write(`\x1b[${up}A\x1b[${rows}M${restore}`)
}

/**
 * Erase a previously printed clack log/spinner line from the terminal via
 * ANSI delete-line, shifting any rows printed after it upward. `linesBelow` is
 * the number of settled clack lines printed since the target line — callers
 * must know exactly what followed it; a wrapped line breaks the row math, so
 * only use this for short messages. No-op off-TTY (CI, pipes) where cursor
 * movement would corrupt plain output.
 */
export function eraseClackLine(options: { linesBelow?: number } = {}): void {
  if (!process.stdout.isTTY || process.env.CI === 'true') return
  const below = (options.linesBelow ?? 0) * CLACK_LINE_ROWS
  const up = below + CLACK_LINE_ROWS
  const restore = below > 0 ? `\x1b[${below}B` : ''
  process.stdout.write(`\x1b[${up}A\x1b[${CLACK_LINE_ROWS}M${restore}`)
}

let activeSpinners = 0

/**
 * While a wrapped spinner animates, clack's spinner prints its own
 * `cancelMessage` line from a `process.on('exit')` hook, so a SIGINT handler
 * that exits the process must not print a second cancellation line (see
 * `installSetupCancelGuard`).
 */
export function hasActiveSpinner(): boolean {
  return activeSpinners > 0
}

/**
 * Drop-in replacement for `p.spinner()`. Animated text (`start`, `message`)
 * is width-limited; `stop` text is printed once and left to wrap naturally.
 * `clear()` erases the spinner without a final line — including the leading
 * `│` guide line clack writes at `start()` and leaves behind on its own
 * `clear()` — so a cleared spinner leaves no trace at all.
 */
export function spinner(options?: { withGuide?: boolean }): Spinner {
  // cancelMessage is what clack prints for a spinner interrupted by a signal
  // or process exit — match the prompts' cancellation wording.
  const inner = p.spinner({ ...options, cancelMessage: 'Setup cancelled.' })
  const guided = options?.withGuide !== false
  let started = false
  const setStarted = (next: boolean) => {
    if (started === next) return
    started = next
    activeSpinners += next ? 1 : -1
  }
  return {
    start: (message = '') => {
      // clack's start() while already spinning leaks the previous animation
      // interval — it can never be cleared and ticks over all later output —
      // so a re-start only swaps the message.
      if (started) {
        inner.message(fitSpinnerMessage(message))
        return
      }
      setStarted(true)
      inner.start(fitSpinnerMessage(message))
    },
    message: (message = '') => inner.message(fitSpinnerMessage(message)),
    stop: (message = '') => {
      setStarted(false)
      inner.stop(message)
    },
    clear: () => {
      if (!started) return
      setStarted(false)
      inner.clear()
      if (guided && process.stdout.isTTY && process.env.CI !== 'true') {
        process.stdout.write('\x1b[1A\x1b[2K')
      }
    }
  }
}

/**
 * Make Ctrl-C during setup print `Setup cancelled.` and exit 0 instead of
 * Node's default hard kill (or, worse, clack's spinner swallowing the signal
 * and letting the flow keep running half-cancelled).
 *
 * Signal coverage by state:
 * - Inside a clack prompt, stdin is in raw mode, so Ctrl-C never raises a
 *   signal — the prompt returns its cancel symbol and the existing
 *   `isCancel`/`onCancel` paths print the same message.
 * - While a spinner is animating, clack's spinner prints its `cancelMessage`
 *   line from a `process.on('exit')` hook, so this handler exits silently to
 *   avoid a duplicate line.
 * - Anywhere else (child processes with inherited stdio, gaps between
 *   steps), this handler prints the message itself.
 *
 * Returns a dispose function. Callers must dispose before handing signal
 * ownership to a long-lived child — the managed dev server forwards
 * SIGINT/SIGTERM to its child and must outlive the first Ctrl-C.
 */
export function installSetupCancelGuard(): () => void {
  const onSignal = () => {
    if (!hasActiveSpinner()) {
      p.cancel('Setup cancelled.')
    }
    process.exit(0)
  }
  process.on('SIGINT', onSignal)
  process.on('SIGTERM', onSignal)
  return () => {
    process.off('SIGINT', onSignal)
    process.off('SIGTERM', onSignal)
  }
}

export function addVersionToBoxBottomBorder(box: string, version: string): string {
  const lines = box.split('\n')
  const bottomLineIndex = lines.length - 2
  const bottomLine = lines[bottomLineIndex]

  if (!bottomLine) {
    return box
  }

  const leftCornerIndex = bottomLine.indexOf(p.S_CORNER_BOTTOM_LEFT)
  const rightCornerIndex = bottomLine.lastIndexOf(p.S_CORNER_BOTTOM_RIGHT)
  const label = ` v${version} `
  const borderWidth = rightCornerIndex - leftCornerIndex - p.S_CORNER_BOTTOM_LEFT.length

  if (leftCornerIndex === -1 || rightCornerIndex === -1 || borderWidth < label.length) {
    return box
  }

  const leftBorderWidth = Math.floor((borderWidth - label.length) / 2)
  const rightBorderWidth = borderWidth - label.length - leftBorderWidth
  lines[bottomLineIndex] =
    `${bottomLine.slice(0, leftCornerIndex + p.S_CORNER_BOTTOM_LEFT.length)}${p.S_BAR_H.repeat(leftBorderWidth)}${label}${p.S_BAR_H.repeat(rightBorderWidth)}${bottomLine.slice(rightCornerIndex)}`

  return lines.join('\n')
}

export function cancelAndExit(message: string): never {
  p.cancel(message)
  process.exit(0)
}

export function restyleStepSubmitSymbols(text: string): string {
  return text.split(GREEN_STEP_SUBMIT).join(GREEN_CHECK)
}

let installed = false

/**
 * Install this BEFORE any other `process.stdout.write` patch that gets restored
 * mid-run — notably `redirectStdoutToStderr`. Because `installed` latches until
 * the returned restore runs, a patch restored on top of this one wipes it out
 * permanently rather than temporarily.
 */
export function installPromptCheckmarks(): () => void {
  if (installed || !process.stdout.isTTY || process.env.CI === 'true') return () => {}
  installed = true

  const unpatched = process.stdout.write
  const write = unpatched.bind(process.stdout) as (
    chunk: string | Uint8Array,
    encoding?: unknown,
    callback?: unknown
  ) => boolean

  process.stdout.write = ((chunk: string | Uint8Array, encoding?: unknown, callback?: unknown) => {
    if (typeof chunk !== 'string') return write(chunk, encoding, callback)
    return write(restyleStepSubmitSymbols(chunk), encoding, callback)
  }) as typeof process.stdout.write

  return () => {
    process.stdout.write = unpatched
    installed = false
  }
}

/**
 * JSON-mode commands call this before any human output so stdout stays
 * reserved for the final machine-readable payload.
 *
 * The returned restore reinstates whatever write was current at install time,
 * so this must be composed LIFO with any other `process.stdout.write` patch
 * (see `installPromptCheckmarks`). Restoring out of order silently discards the
 * patch installed after this one.
 */
export function redirectStdoutToStderr(): () => void {
  const originalWrite = process.stdout.write.bind(process.stdout)
  process.stdout.write = ((
    chunk: Parameters<typeof process.stdout.write>[0],
    encodingOrCallback?: BufferEncoding | ((error?: Error | null) => void),
    callback?: (error?: Error | null) => void
  ) =>
    typeof encodingOrCallback === 'function'
      ? process.stderr.write(chunk, encodingOrCallback)
      : process.stderr.write(chunk, encodingOrCallback, callback)) as typeof process.stdout.write

  return () => {
    process.stdout.write = originalWrite
  }
}

export function renderTableRows(rows: string[][]): string[] {
  const widths: number[] = []
  for (const row of rows) {
    row.forEach((cell, index) => {
      if (index < row.length - 1) {
        widths[index] = Math.max(widths[index] ?? 0, cell.length)
      }
    })
  }

  return rows.map((row) =>
    row
      .map((cell, index) => (index < row.length - 1 ? cell.padEnd(widths[index]) : cell))
      .join('  ')
      .trimEnd()
  )
}
